import React from "react";
import {OrderEditorTabProps} from "@widgets/pos/order/OrderEditor";
import CardHeader from "peggirkit/dist/components/Card/CardHeading/CardHeader";
import {Alert, Card, InputLabel, SimpleCardHeading, toggleDescriptionLabel, ToggleGroup} from "peggirkit";
import {FormattedMessage, useIntl} from "react-intl";
import {useFormContext, useWatch} from "react-hook-form";
import {PosOrderForm} from "@/data/entities/order";
import {useFulfillmentType} from "@widgets/pos/order/hooks/UseFulfillmentType";
import {useExistingOrder} from "@widgets/pos/order/hooks/UseExistingOrder";
import {formatMoney} from "@/utils/MoneyUtils";

/**
 * Payment of an order paid in person: still to pay (PIN) at the door or at
 * pickup, or already paid in the restaurant. Not shown for orders paid
 * online: their payment is shown in the totals.
 */
const PaymentCard = ({isPending}: OrderEditorTabProps) => {
    const intl = useIntl();
    const {register, control, formState: {errors}} = useFormContext<PosOrderForm>();
    const paymentStatus = useWatch<PosOrderForm, "paymentStatus">({control, name: "paymentStatus"});
    const fulfillmentType = useFulfillmentType();
    const existingOrder = useExistingOrder();

    if (existingOrder !== null && existingOrder.paymentMethod !== "IN_PERSON")
        return null;

    // Total when the order was paid in the restaurant, null when not paid (yet)
    const paidTotal: number | null = existingOrder?.order.paidInPerson ? existingOrder.totalToPay : null;

    return (
        <Card
            header={
                <CardHeader>
                    <SimpleCardHeading title={<FormattedMessage id={"payment"}/>}/>
                </CardHeader>
            }
        >
            <div className={"flex flex-col gap-4"}>
                <ToggleGroup
                    type={"radio"}
                    label={<InputLabel><FormattedMessage id={"paymentStatus"}/></InputLabel>}
                    toggles={[
                        {
                            value: "PAY_LATER",
                            label: toggleDescriptionLabel(
                                <FormattedMessage
                                    id={fulfillmentType === "DELIVERY" ? "payOnDelivery" : "payOnTakeaway"}/>,
                                <FormattedMessage id={fulfillmentType === "DELIVERY"
                                    ? "customerPaysAtDoorstep"
                                    : "customerPaysAtPickup"}/>,
                            ),
                        },
                        {
                            value: "PAID",
                            label: toggleDescriptionLabel(
                                <FormattedMessage id={"paidInRestaurant"}/>,
                                <FormattedMessage id={"paidInRestaurantDescription"}/>,
                            ),
                        },
                    ]}
                    groupName={"paymentStatus"}
                    togglesProps={{
                        ...register("paymentStatus", {
                            required: intl.formatMessage({id: "pick-required-option"}),
                        }),
                    }}
                    disabled={isPending}
                    error={errors.paymentStatus && errors.paymentStatus.message}
                />

                {
                    // Already paid: a changed total has to be settled at the counter
                    paidTotal !== null && paymentStatus === "PAID" &&
                    <Alert
                        type={"warning"}
                        title={<FormattedMessage id={"alreadyPaidInRestaurant"}/>}
                        content={
                            <FormattedMessage
                                id={"alreadyPaidInRestaurantDescription"}
                                values={{amount: formatMoney(paidTotal)}}
                            />
                        }
                    />
                }
            </div>
        </Card>
    );
};

export default PaymentCard;